import { Button, Typography } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type BoundingBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type SignatureField = {
  bbox: BoundingBox;
  imageDataUrl?: string;
};

const SIGNATURE_PAD_PADDING = 8;
const SIGNATURE_PAD_BACKGROUND_COLOR = "#ffffff";
const SIGNATURE_PAD_PEN_COLOR = "#000000";
const DEFAULT_SIGNATURE_ASPECT_RATIO = 3;

const getSignatureAspectRatio = (bbox?: BoundingBox) => {
  if (!bbox || bbox.width <= 0 || bbox.height <= 0) {
    return DEFAULT_SIGNATURE_ASPECT_RATIO;
  }

  return bbox.width / bbox.height;
};

const getSignatureGuideSize = ({
  aspectRatio,
  containerHeight,
  containerWidth,
}: {
  aspectRatio: number;
  containerHeight: number;
  containerWidth: number;
}) => {
  const maxWidth = Math.max(containerWidth - SIGNATURE_PAD_PADDING * 2, 1);
  const maxHeight = Math.max(containerHeight - SIGNATURE_PAD_PADDING * 2, 1);

  if (maxWidth / maxHeight > aspectRatio) {
    const height = maxHeight;
    return { width: height * aspectRatio, height };
  }

  const width = maxWidth;
  return { width, height: width / aspectRatio };
};

type Point = {
  x: number;
  y: number;
};

export function ExtendSignaturePadDialog({
  field,
  onCancel,
  onConfirm,
}: {
  field: SignatureField;
  onCancel: () => void;
  onConfirm: (value: string) => void;
}) {
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<Point | null>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [guideSize, setGuideSize] = useState<{
    height: number;
    width: number;
  } | null>(null);
  const signatureAspectRatio = useMemo(
    () => getSignatureAspectRatio(field.bbox),
    [field.bbox],
  );

  const syncCanvasSize = (canvas: HTMLCanvasElement) => {
    const width = Math.max(canvas.offsetWidth, 1);
    const height = Math.max(canvas.offsetHeight, 1);
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    const context = canvas.getContext("2d");
    context?.setTransform(1, 0, 0, 1, 0, 0);
    context?.scale(ratio, ratio);

    return { width, height, ratio };
  };

  const redrawFromDataUrl = async (dataUrl: string) => {
    const canvas = canvasRef.current;
    if (!canvas || !dataUrl) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => {
        resolve();
      };
      image.onerror = () => {
        reject(new Error("Unable to restore signature"));
      };
      image.src = dataUrl;
    });

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    let frameId = 0;
    let resizeObserver: ResizeObserver | null = null;

    const updateGuideSize = (container?: HTMLDivElement | null) => {
      const currentContainer = container ?? canvasContainerRef.current;
      if (
        !currentContainer ||
        currentContainer.clientWidth <= 0 ||
        currentContainer.clientHeight <= 0
      ) {
        return false;
      }

      setGuideSize(
        getSignatureGuideSize({
          containerWidth: currentContainer.clientWidth,
          containerHeight: currentContainer.clientHeight,
          aspectRatio: signatureAspectRatio,
        }),
      );
      return true;
    };

    const connect = () => {
      const container = canvasContainerRef.current;
      if (!updateGuideSize(container)) {
        frameId = window.requestAnimationFrame(connect);
        return;
      }

      if (container) {
        resizeObserver = new ResizeObserver(() => {
          updateGuideSize(container);
        });
        resizeObserver.observe(container);
      }
    };

    connect();

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
    };
  }, [signatureAspectRatio]);

  useEffect(() => {
    if (!guideSize || guideSize.width <= 1 || guideSize.height <= 1) {
      setIsReady(false);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    const previousSignature = canvas.toDataURL("image/png");
    syncCanvasSize(canvas);

    const restore = async () => {
      if (cancelled) return;
      if (field.imageDataUrl) {
        await redrawFromDataUrl(field.imageDataUrl);
        setHasSignature(true);
        setIsReady(true);
        return;
      }

      if (previousSignature !== "data:,") {
        await redrawFromDataUrl(previousSignature);
        setHasSignature(previousSignature !== "data:,");
      }

      setIsReady(true);
    };

    void restore();

    const resizeObserver = new ResizeObserver(() => {
      const currentCanvas = canvasRef.current;
      if (!currentCanvas) return;

      const snapshot = currentCanvas.toDataURL("image/png");
      syncCanvasSize(currentCanvas);
      if (snapshot !== "data:,") {
        void redrawFromDataUrl(snapshot);
      }
    });

    resizeObserver.observe(canvas);

    return () => {
      cancelled = true;
      resizeObserver.disconnect();
      setIsReady(false);
    };
  }, [field.imageDataUrl, guideSize]);

  const drawStroke = useCallback((start: Point, end: Point) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    context.save();
    context.strokeStyle = SIGNATURE_PAD_PEN_COLOR;
    context.lineWidth = 2;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.beginPath();
    context.moveTo(start.x, start.y);
    context.lineTo(end.x, end.y);
    context.stroke();
    context.restore();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isReady) return;

    const getPointFromEvent = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      return { x, y };
    };

    const handlePointerDown = (event: PointerEvent) => {
      const point = getPointFromEvent(event);

      canvas.setPointerCapture(event.pointerId);
      drawingRef.current = true;
      lastPointRef.current = point;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!drawingRef.current) return;
      const nextPoint = getPointFromEvent(event);
      const lastPoint = lastPointRef.current;
      if (!lastPoint) return;

      drawStroke(lastPoint, nextPoint);
      lastPointRef.current = nextPoint;
      setHasSignature(true);
    };

    const handlePointerUp = (event: PointerEvent) => {
      drawingRef.current = false;
      lastPointRef.current = null;
      try {
        canvas.releasePointerCapture(event.pointerId);
      } catch {
        // Ignore pointer capture release errors.
      }
      setHasSignature(canvas.toDataURL("image/png") !== "data:,");
    };

    const handlePointerCancel = () => {
      drawingRef.current = false;
      lastPointRef.current = null;
    };

    canvas.addEventListener("pointerdown", handlePointerDown);
    canvas.addEventListener("pointermove", handlePointerMove);
    canvas.addEventListener("pointerup", handlePointerUp);
    canvas.addEventListener("pointercancel", handlePointerCancel);
    canvas.addEventListener("pointerleave", handlePointerCancel);

    return () => {
      canvas.removeEventListener("pointerdown", handlePointerDown);
      canvas.removeEventListener("pointermove", handlePointerMove);
      canvas.removeEventListener("pointerup", handlePointerUp);
      canvas.removeEventListener("pointercancel", handlePointerCancel);
      canvas.removeEventListener("pointerleave", handlePointerCancel);
    };
  }, [drawStroke, isReady]);

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-card w-full max-w-xl rounded-2xl border p-4 shadow-2xl">
        <div className="mb-4 space-y-1">
          <Typography variant="heading" size="sm">
            Add signature
          </Typography>
          <Typography className="text-muted-foreground text-sm">
            Draw a signature to place it into the selected PDF field.
          </Typography>
        </div>

        <div className="rounded-xl border bg-white p-3 shadow-xs">
          <div
            ref={canvasContainerRef}
            className="flex h-56 w-full items-center justify-center overflow-hidden rounded-lg bg-white p-2"
          >
            <div
              className={cn(
                "relative overflow-hidden rounded-[3px] border border-dashed border-blue-500/70 bg-white",
                isReady ? "cursor-crosshair" : "cursor-wait",
              )}
              style={{
                width: guideSize ? `${guideSize.width}px` : undefined,
                height: guideSize ? `${guideSize.height}px` : undefined,
                opacity: guideSize ? 1 : 0,
                backgroundColor: SIGNATURE_PAD_BACKGROUND_COLOR,
              }}
            >
              <canvas
                ref={canvasRef}
                className={cn(
                  "absolute inset-0 size-full touch-none",
                  !isReady && "pointer-events-none",
                )}
                style={{
                  backgroundColor: SIGNATURE_PAD_BACKGROUND_COLOR,
                  touchAction: "none",
                }}
              />
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={!isReady}
            onClick={() => {
              const canvas = canvasRef.current;
              if (!canvas) return;

              const context = canvas.getContext("2d");
              context?.clearRect(0, 0, canvas.width, canvas.height);
              setHasSignature(false);
            }}
          >
            Clear
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!isReady || !hasSignature}
              onClick={() => {
                const canvas = canvasRef.current;
                if (!canvas) return;

                onConfirm(canvas.toDataURL("image/png"));
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
